<template>
  <div class="container">
    <team-pulse-modals-add-channel
      v-model="showAddChannelModal"
      @submitted="onChannelsAdded"
    />
    <team-pulse-modals-gathering-insights
      v-model="showGatheringInsightsModal"
    />
    <donut-alert
      class="m-bx4"
      theme="info"
      title="Early Access: Team Pulse Beta"
      message="Dive into your team's communication health and sentiment scores at no extra cost. We'll notify you before the beta ends – in the meantime, help us make it better."
      button-text="Is this useful?"
      button-url="https://forms.gle/Yx4LmV12cVY6QDZM7"
      :button-open-in-new-tab="true"
    />
    <donut-page-title
      class="m-bx2"
      title="Team Pulse"
      subtitle="Monitor communication health and channel engagement at a glance with AI. Add any Slack channel to get an instant gauge on participation and sentiment."
    />

    <!-- 90 day Team Pulse Summary -->
    <div class="f f-h f-ast f-gap-16 m-bx4">
      <div class="team-pulse-card team-pulse-main p-x2">
        <div class="f f-h f-ac f-ast m-bx2">
          <div class="f f-v f-gap-8 m-rx2">
            <cdn-svg
              :path="engagementBarsPath"
              :version="0"
              class="h-x4 w-x4"
            />
            <cdn-svg
              :path="sentimentFacePath"
              :version="0"
              class="h-x4 w-x4 m-tx2"
            />
          </div>
          <div class="f-v f-grow m-rx2">
            <div class="t-lg bold">90 day Team Pulse</div>
            <div class="t-ml display m-tx1">
              <div v-if="isLoadingSummary" class="f f-h f-ac">
                <donut-loading-dots />
              </div>
              <div v-else-if="teamPulseSummary && teamPulseSummary.summary">
                {{ teamPulseSummary.summary }}
              </div>
              <div v-else>
                There is no engagement data available to generate a summary.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <team-pulse-legend />
    </div>

    <!-- Weekly channel pulse -->
    <div class="m-bx4">
      <div class="f f-h f-ac f-sb m-bx4">
        <div class="t-lg bold">Weekly channel pulse</div>
        <div class="f-h">
          <div class="search-bar w-x30">
            <lucide-icon name="Search" :stroke-width="2" class="purple search-icon" :size="18" />
            <input
              v-model="channelSearchQuery"
              name="q"
              placeholder="Search for a channel..."
            >
          </div>
          <div class="button inv m-lx2" @click="openAddChannelModal">
            <div class="f-h f-ac">
              <i class="mi">add</i>
              <span class="m-lx1">Add channel pulse</span>
            </div>
          </div>
        </div>
      </div>

      <div class="t m-bx2">Click on any channel below to see its participation and sentiment trends.</div>

      <div v-if="filteredChannelPulseReports.length > 0" class="f-v f-gap-16">
        <team-pulse-channel-card
          v-for="channelPulseReport in filteredChannelPulseReports"
          :key="channelPulseReport.channel.id"
          :channel-pulse-report="channelPulseReport"
        />
      </div>

      <donut-empty
        v-else
        :title="channelSearchQuery ? `This channel isn't tracked yet` : 'No channels configured'"
        :description="channelSearchQuery ? 'Use the button above to add it and start tracking participation and sentiment.' : 'Add channels to start monitoring participation and sentiment.'"
        :action-label="channelSearchQuery ? null : 'Add channel pulse'"
        image="nucleo/ghost"
        :action="channelSearchQuery ? null : openAddChannelModal"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import LucideIcon from 'components/lucide-icon.vue';
import useEngagementReportIcons from 'composables/team-pulse/icons';

const props = defineProps({
  channelPulseReports: {
    type: Array,
    default: () => [],
  },
});

const store = useStore();
const channelSearchQuery = ref('');
const CHANNEL_PULSE_POLL_INTERVAL_MS = 30000;
let channelPulsePolling = null;

const normalizeChannelPulseReports = (channelPulseReports) => {
  if (Array.isArray(channelPulseReports)) return channelPulseReports;
  if (channelPulseReports && typeof channelPulseReports === 'object') {
    return Object.values(channelPulseReports);
  }
  return [];
};

const allChannelPulseReports = ref(normalizeChannelPulseReports(props.channelPulseReports));

const hasGeneratingChannelPulseReports = computed(() => allChannelPulseReports.value
  .some((report) => report?.state === 'generating'));

const stopPollingForChannelPulseReports = () => {
  if (!channelPulsePolling) return;

  clearInterval(channelPulsePolling);
  channelPulsePolling = null;
};

const refreshChannelPulseReports = async () => {
  const channelPulseReports = await store.dispatch('teamPulse/fetchChannelPulseReports');

  if (Array.isArray(channelPulseReports)) {
    allChannelPulseReports.value = channelPulseReports;
  }

  if (!hasGeneratingChannelPulseReports.value) {
    stopPollingForChannelPulseReports();
  }
};

const startPollingForChannelPulseReports = () => {
  if (channelPulsePolling || !hasGeneratingChannelPulseReports.value) return;

  channelPulsePolling = setInterval(refreshChannelPulseReports, CHANNEL_PULSE_POLL_INTERVAL_MS);
};

// Fetch team pulse summary on mount
onMounted(() => {
  store.dispatch('teamPulse/fetchTeamPulseSummary');
  startPollingForChannelPulseReports();
});

const teamPulseSummary = computed(() => store.state.teamPulse.teamPulseSummary);
const isLoadingSummary = computed(() => store.state.teamPulse.loading);
const filteredChannelPulseReports = computed(() => {
  const query = channelSearchQuery.value.trim().toLowerCase();
  if (!query) return allChannelPulseReports.value;

  return allChannelPulseReports.value.filter((report) => {
    const name = report?.channel?.name || '';
    return String(name).toLowerCase().includes(query);
  });
});

// Use composable for engagement report icons
const { engagementBarsPath, sentimentFacePath } = useEngagementReportIcons(
  computed(() => teamPulseSummary.value || {}),
);

const showAddChannelModal = ref(false);
const showGatheringInsightsModal = ref(false);

const openAddChannelModal = () => {
  showAddChannelModal.value = true;
};

const onChannelsAdded = () => {
  showGatheringInsightsModal.value = true;
};
</script>

<style scoped lang="scss">
@import '../../styles/donut';

.team-pulse-card {
  border: none;
  border-radius: 12px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
}

.team-pulse-main {
  flex: 2;
  min-width: 0; // Allows flex item to shrink below content size
  background-color: $purple-bg-light;
}
</style>

